import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { apiClient } from '../api/apiClient';

interface AbpApplicationConfiguration {
  auth: {
    grantedPolicies: Record<string, boolean>;
  };
  currentUser?: {
    isAuthenticated: boolean;
    id?: string;
    userName?: string;
    email?: string;
    roles?: string[];
  };
  setting?: {
    values: Record<string, string>;
  };
}

interface AbpPermissionsContextValue {
  permissions: Record<string, boolean>;
  settings: Record<string, string>;
  hasPermission: (permission: string) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  getSetting: (settingName: string, defaultValue?: string) => string;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

const AbpPermissionsContext = createContext<AbpPermissionsContextValue | undefined>(undefined);

interface AbpPermissionsProviderProps {
  children: ReactNode;
}

export function AbpPermissionsProvider({ children }: AbpPermissionsProviderProps) {
  const auth = useAuth();
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const isFetchingRef = useRef(false); // Track if we're currently fetching to prevent duplicates

  const fetchPermissions = async () => {
    // Prevent duplicate requests (React StrictMode protection)
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);

      // Fetch ABP configuration (works for both authenticated and anonymous users)
      // Anonymous users get public settings (isVisibleToClients: true) but no permissions
      const response = await apiClient.get<AbpApplicationConfiguration>(
        '/api/abp/application-configuration'
      );

      const grantedPolicies = response.data.auth?.grantedPolicies || {};
      const settingValues = response.data.setting?.values || {};

      setPermissions(grantedPolicies);
      setSettings(settingValues);

      console.log('[AbpPermissions] Configuration fetched. Authenticated:', auth.isAuthenticated, 'Settings count:', Object.keys(settingValues).length);
    } catch (err) {
      console.error('Failed to fetch ABP configuration:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      setPermissions({});
      setSettings({});
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  // Fetch configuration on mount and when auth state changes
  // - On mount (unauthenticated): Fetches public settings (for white labeling)
  // - After login (authenticated): Fetches settings + permissions
  useEffect(() => {
    fetchPermissions();
  }, [auth.isAuthenticated]);

  const hasPermission = (permission: string): boolean => {
    return permissions[permission] === true;
  };

  const hasAllPermissions = (requiredPermissions: string[]): boolean => {
    return requiredPermissions.every((perm) => hasPermission(perm));
  };

  const hasAnyPermission = (requiredPermissions: string[]): boolean => {
    return requiredPermissions.some((perm) => hasPermission(perm));
  };

  const getSetting = (settingName: string, defaultValue: string = ''): string => {
    return settings[settingName] ?? defaultValue;
  };

  const value: AbpPermissionsContextValue = {
    permissions,
    settings,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    getSetting,
    isLoading,
    error,
    refresh: fetchPermissions,
  };

  return (
    <AbpPermissionsContext.Provider value={value}>
      {children}
    </AbpPermissionsContext.Provider>
  );
}

export function useAbpPermissions(): AbpPermissionsContextValue {
  const context = useContext(AbpPermissionsContext);
  if (context === undefined) {
    throw new Error('useAbpPermissions must be used within an AbpPermissionsProvider');
  }
  return context;
}

// Helper hook for checking a single permission
export function useHasPermission(permission: string): boolean {
  const { hasPermission } = useAbpPermissions();
  return hasPermission(permission);
}

// Helper hook for checking multiple permissions (ALL required)
export function useHasAllPermissions(permissions: string[]): boolean {
  const { hasAllPermissions } = useAbpPermissions();
  return hasAllPermissions(permissions);
}

// Helper hook for checking multiple permissions (ANY required)
export function useHasAnyPermission(permissions: string[]): boolean {
  const { hasAnyPermission } = useAbpPermissions();
  return hasAnyPermission(permissions);
}
